/**
 * Lo que hace que ninguna llamada de la app se pueda colgar para siempre.
 *
 * # El agujero, verificado leyendo el bundle
 *
 * `@tiendanube/nexo` 1.3.1, su `asyncAction` —de donde sale `getSessionToken`— es:
 *
 *     asyncAction = (nexo, accion, payload) => new Promise((resolver) => {
 *       const desuscribir = nexo.suscribe(accion, (r) => { resolver(r); desuscribir(); });
 *       nexo.dispatch(message(accion, payload));
 *     })
 *
 * **No hay `reject` y no hay timeout.** Si el admin no contesta ese `postMessage` una sola
 * vez, la promesa no se resuelve nunca. Y como cada llamada al backend empieza pidiendo un
 * token, la pantalla que esperaba ese dato se queda cargando para siempre: sin error, sin
 * log y sin nada que reintentar.
 *
 * Es el modo de falla más caro que hay, porque **no se distingue de "está tardando"**. El
 * comerciante espera, se cansa y se va; nosotros no nos enteramos nunca, porque el request
 * ni siquiera salió del navegador y no hay nada que mirar de nuestro lado.
 *
 * # Por qué vive en su propio módulo
 *
 * Para poder probarlo. `api.ts` importa Nexo, que toca `window` al cargarse, así que
 * cualquier test que lo importe revienta en Node. Acá adentro no hay ni una dependencia:
 * las dos funciones reciben por parámetro lo que usan.
 */

/** Se lanza cuando algo no contestó a tiempo. `api.ts` lo traduce a un `ApiError`. */
export class VencimientoError extends Error {
  constructor(public readonly queEs: string) {
    super(`${queEs} no respondió a tiempo.`);
    this.name = 'VencimientoError';
  }
}

/**
 * Un pedidor de token con **un solo pedido en vuelo**, compartido por todos los que llamen
 * a la vez, y con vencimiento.
 *
 * *** Lo de compartir no es una optimización. *** Al montar, una pantalla dispara varias
 * llamadas juntas —el home son cuatro: sesión, inicio, corridas y programaciones— y cada
 * una pedía su propio token: cuatro `postMessage` en el mismo tick contra un admin que
 * puede contestar uno solo. Compartir la promesa deja un pedido, y le da el mismo token a
 * todos, que además es lo correcto: el token no depende de quién lo pida.
 *
 * *** Se comparte la promesa en vuelo, no el resultado. *** El token vence; cachearlo
 * cambiaría un cuelgue por un 401 misterioso media hora después. Apenas termina —bien o
 * mal— el próximo que llame arranca uno nuevo.
 */
export function crearPedidorDeToken(
  obtener: () => Promise<string>,
  esperaMaxMs: number,
  queEs = 'El panel de Tienda Nube',
): () => Promise<string> {
  let enVuelo: Promise<string> | null = null;

  return () => {
    enVuelo ??= conVencimiento(obtener(), esperaMaxMs, queEs).finally(() => {
      enVuelo = null;
    });
    return enVuelo;
  };
}

/**
 * La promesa, o `VencimientoError` a los `ms`.
 *
 * No cancela nada: no hay forma de cancelar un `postMessage` que nadie contestó. Lo que
 * hace es dejar de esperarlo, que es todo lo que hace falta para que la pantalla pueda
 * mostrar algo.
 */
export function conVencimiento<T>(promesa: Promise<T>, ms: number, queEs: string): Promise<T> {
  return new Promise<T>((resolver, rechazar) => {
    const id = setTimeout(() => {
      rechazar(new VencimientoError(queEs));
    }, ms);

    promesa.then(
      (valor) => {
        clearTimeout(id);
        resolver(valor);
      },
      (error: unknown) => {
        clearTimeout(id);
        rechazar(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/**
 * `fetch` que se corta solo.
 *
 * Un Worker que no contesta deja el `fetch` esperando sin límite, y otra vez la pantalla no
 * puede distinguirlo de "está tardando". El tope es generoso a propósito: lo que se corta
 * no es la lentitud —el preview de un catálogo grande tarda de verdad— sino el cuelgue.
 *
 * Devuelve el mismo `Response` que `fetch`. Si vence, lanza `VencimientoError`: un `abort`
 * pelado tira un `DOMException` que arriba se confunde con un error de red cualquiera.
 */
export async function fetchConVencimiento(
  fetchImpl: typeof fetch,
  entrada: string,
  init: RequestInit,
  ms: number,
  queEs = 'El servidor',
): Promise<Response> {
  const cortar = new AbortController();
  const id = setTimeout(() => {
    cortar.abort();
  }, ms);

  try {
    return await fetchImpl(entrada, { ...init, signal: cortar.signal });
  } catch (error) {
    if (cortar.signal.aborted) throw new VencimientoError(queEs);
    throw error;
  } finally {
    clearTimeout(id);
  }
}
